import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STALE_MS } from '../config';
import { getWorkspaceState, putWorkspaceState } from '../endpoints';
import { isApiError } from '../errors';
import { queryKeys } from '../queryKeys';
import type { WorkspaceStateDocument, WorkspaceStateKind } from '../types';
import { queryRetry } from './shared';

export const STATE_VERSION_CONFLICT = 'version_conflict';

export interface WorkspaceStateResult<TPayload> {
  readonly document: WorkspaceStateDocument<TPayload> | null;
  readonly payload: TPayload | null;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly error: unknown;
  readonly saveError: unknown;
  // True after a 409: someone else wrote first; the fresh document is refetched.
  readonly conflict: boolean;
  // Replace the payload. Applies optimistically, rolls back on failure.
  readonly save: (nextPayload: TPayload) => Promise<WorkspaceStateDocument<TPayload> | null>;
  // Immutable update helper: receives the current payload, returns the next one.
  readonly update: (updater: (current: TPayload) => TPayload) => Promise<WorkspaceStateDocument<TPayload> | null>;
  readonly refetch: () => void;
}

interface MutationContext<TPayload> {
  readonly previous: WorkspaceStateDocument<TPayload> | undefined;
}

// GET and PUT /api/beta/state/:kind (api-surface 2.1). PUT carries
// expectedVersion; the server answers 409 version_conflict on a stale write.
export function useWorkspaceState<TPayload = Record<string, unknown>>(
  kind: WorkspaceStateKind,
  options: { readonly enabled?: boolean; readonly emptyPayload: TPayload },
): WorkspaceStateResult<TPayload> {
  const queryClient = useQueryClient();
  const key = queryKeys.workspaceState(kind);

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => getWorkspaceState<TPayload>(kind, signal),
    enabled: options.enabled ?? true,
    staleTime: STALE_MS.workspaceState,
    retry: queryRetry,
  });

  const mutation = useMutation<WorkspaceStateDocument<TPayload>, unknown, TPayload, MutationContext<TPayload>>({
    mutationFn: async (nextPayload) => {
      const current = queryClient.getQueryData<WorkspaceStateDocument<TPayload>>(key);
      return putWorkspaceState<TPayload>(kind, { payload: nextPayload, expectedVersion: current?.version ?? 0 });
    },
    onMutate: async (nextPayload) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<WorkspaceStateDocument<TPayload>>(key);
      queryClient.setQueryData<WorkspaceStateDocument<TPayload>>(key, {
        kind,
        payload: nextPayload,
        version: previous?.version ?? 0,
      });
      return { previous };
    },
    onError: (_error, _next, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      else queryClient.removeQueries({ queryKey: key });
    },
    onSuccess: (document) => {
      queryClient.setQueryData(key, document);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const { mutateAsync } = mutation;
  const save = useCallback(async (nextPayload: TPayload) => {
    try {
      return await mutateAsync(nextPayload);
    } catch {
      // The error is exposed through saveError; callers read it from the hook.
      return null;
    }
  }, [mutateAsync]);

  const update = useCallback((updater: (current: TPayload) => TPayload) => {
    const current = queryClient.getQueryData<WorkspaceStateDocument<TPayload>>(key)?.payload ?? options.emptyPayload;
    return save(updater(current));
  }, [key, options.emptyPayload, queryClient, save]);

  const conflict = isApiError(mutation.error) && mutation.error.status === 409;

  return {
    document: query.data ?? null,
    payload: query.data?.payload ?? null,
    isLoading: query.isPending,
    isSaving: mutation.isPending,
    error: query.error,
    saveError: mutation.error,
    conflict,
    save,
    update,
    refetch: () => void query.refetch(),
  };
}
